import type { City } from "../types";
import type { AtlasConfig } from "./atlas-config";

/** Stable atlas placement: assign every city an integer minzoom at which it appears,
 *  computed GLOBALLY in world coordinates — never against the current viewport.
 *
 *  MapLibre's collision engine re-runs placement against whatever is on screen, so
 *  panning changes which of two nearby same-priority cities wins (Lancaster vanishes
 *  as Johnstown scrolls in — feels arbitrary because the tie-break is invisible).
 *  A printed atlas decides its label set per SCALE, not per view: this module does the
 *  same. For each integer zoom, cities are admitted in priority order (concert count,
 *  id as the deterministic tie-break) if their estimated label box doesn't overlap any
 *  already-admitted box at that zoom. Once admitted at z, a city is present at every
 *  deeper zoom. Panning can then never change the visible set; zooming only adds.
 *
 *  Boxes are ESTIMATES (glyph metrics aren't available here) — MapLibre's collision
 *  stays on underneath as a rare backstop, sized by the `stablePadding` lever: raise it
 *  for a sparser, calmer map; lower it for a denser one. */

const WORLD = 512; // MapLibre world size at z0, px

/** Bands run in QUARTER-zoom steps: packing admits what fits at the band's start, and
 *  the admitted set is shown until the next band — integer steps meant the z6.0 set was
 *  all you got at z6.9, which read as far too sparse next to dynamic collision. At 0.25
 *  the packing is never more than a 19% scale-step behind the rendered zoom. */
const MIN_Z = 1;
const MAX_Z = 10;
const BAND_STEP = 0.25;

/** Clamp a live zoom to its band (used by markers.ts). */
export function bandFor(zoom: number): number {
  return Math.max(MIN_Z, Math.min(MAX_Z, Math.floor(zoom / BAND_STEP) * BAND_STEP));
}

/** Normalized web-mercator coordinates (0..1). Exported because the residual stipple
 *  grid-caps in the same world-pixel space the packing uses — a cap computed in screen
 *  space would re-shuffle the texture on every pan. */
export function mercX(lon: number): number {
  return (lon + 180) / 360;
}
export function mercY(lat: number): number {
  const clamped = Math.max(-85, Math.min(85, lat));
  const s = Math.sin((clamped * Math.PI) / 180);
  return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
}

/** Rough text width: CJK-and-friends count as full-em, everything else ~0.58em.
 *  Same spirit as the tile-side label estimates — close enough for box packing. */
function textWidth(name: string, size: number): number {
  let w = 0;
  for (const ch of name) {
    w += (ch.codePointAt(0) ?? 0) > 0x2e80 ? size : 0.6 * size;
  }
  return w;
}

/** Mirrors the per-tier text-size zoom curves in markers.ts. */
const SIZE_STOPS: Record<number, [number, number][]> = {
  1: [[3, 11], [6, 14.5], [10, 18]],
  2: [[3, 10], [6, 12.5], [10, 15]],
  3: [[3, 9.5], [6, 11.5], [10, 14]],
};

function sizeAt(tier: number, z: number, labelScale: number): number {
  const stops = SIZE_STOPS[tier] ?? SIZE_STOPS[3];
  if (z <= stops[0][0]) return stops[0][1] * labelScale;
  for (let i = 1; i < stops.length; i++) {
    if (z <= stops[i][0]) {
      const [z0, s0] = stops[i - 1];
      const [z1, s1] = stops[i];
      return (s0 + ((s1 - s0) * (z - z0)) / (z1 - z0)) * labelScale;
    }
  }
  return stops[stops.length - 1][1] * labelScale;
}

/** Mirrors the icon-size zoom curve (0.72 at z3 → 1.0 at z6) and tier diameters. */
const TIER_RADIUS = { 1: 7.5, 2: 5, 3: 3.75 };
function iconRadiusAt(tier: number, z: number, sizeScale: number): number {
  const t = Math.max(0, Math.min(1, (z - 3) / 3));
  return (TIER_RADIUS[tier as 1 | 2 | 3] ?? 3.75) * (0.72 + 0.28 * t) * sizeScale;
}

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Simple uniform-grid box index (cells in px at the current zoom's world scale). */
class BoxGrid {
  private cells = new Map<string, Box[]>();
  private cell: number;

  constructor(cellSize: number) {
    this.cell = cellSize;
  }

  private *keys(b: Box): Generator<string> {
    const cx0 = Math.floor(b.x0 / this.cell);
    const cx1 = Math.floor(b.x1 / this.cell);
    const cy0 = Math.floor(b.y0 / this.cell);
    const cy1 = Math.floor(b.y1 / this.cell);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        yield `${cx}:${cy}`;
      }
    }
  }

  collides(b: Box): boolean {
    for (const key of this.keys(b)) {
      const boxes = this.cells.get(key);
      if (!boxes) continue;
      for (const o of boxes) {
        if (b.x0 < o.x1 && b.x1 > o.x0 && b.y0 < o.y1 && b.y1 > o.y0) return true;
      }
    }
    return false;
  }

  insert(b: Box): void {
    for (const key of this.keys(b)) {
      let boxes = this.cells.get(key);
      if (!boxes) {
        boxes = [];
        this.cells.set(key, boxes);
      }
      boxes.push(b);
    }
  }
}

/** Point index for the isolation exemption: "is any admitted marker within r px?".
 *  Cell size is the query radius, so a 3×3 neighbourhood always covers the disc. */
class PointGrid {
  private cells = new Map<string, [number, number][]>();
  private cell: number;

  constructor(radius: number) {
    this.cell = Math.max(1, radius);
  }

  insert(x: number, y: number): void {
    const key = `${Math.floor(x / this.cell)}:${Math.floor(y / this.cell)}`;
    let pts = this.cells.get(key);
    if (!pts) {
      pts = [];
      this.cells.set(key, pts);
    }
    pts.push([x, y]);
  }

  hasWithin(x: number, y: number, r: number): boolean {
    const cx = Math.floor(x / this.cell);
    const cy = Math.floor(y / this.cell);
    const r2 = r * r;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const pts = this.cells.get(`${cx + dx}:${cy + dy}`);
        if (!pts) continue;
        for (const [px, py] of pts) {
          const ddx = px - x;
          const ddy = py - y;
          if (ddx * ddx + ddy * ddy <= r2) return true;
        }
      }
    }
    return false;
  }
}

/** The label+icon footprint of a city at zoom z, in world px at that zoom. Stable mode
 *  pins the label to a FIXED anchor (name to the right of the dot — markers.ts sets
 *  text-anchor "left" when placement is stable), so this box models the real footprint
 *  closely: dot on the left, then the text run. That fidelity is the point — with
 *  variable anchors the real engine can settle labels into positions this packing never
 *  considered, and the viewport gets to arbitrate again (the exact bug being fixed). */
/** Anchor codes stored in the `aside` feature property. Values are MapLibre
 *  text-anchor semantics: "left" puts the text right of the dot, "top" below it. */
export const ANCHOR_LEFT = 0; // text right of dot
export const ANCHOR_RIGHT = 1; // text left of dot
export const ANCHOR_TOP = 2; // text below dot
export const ANCHOR_BOTTOM = 3; // text above dot
export type AnchorCode = 0 | 1 | 2 | 3;
/** Candidate order mirrors the aesthetic preference: beside the dot first. */
const ANCHOR_CANDIDATES: AnchorCode[] = [ANCHOR_LEFT, ANCHOR_RIGHT, ANCHOR_TOP, ANCHOR_BOTTOM];

function boxAt(
  city: City,
  tier: number,
  z: number,
  cfg: AtlasConfig,
  scale: number,
  side: AnchorCode
): Box {
  const m = cfg.markers;
  const size = sizeAt(tier, z, m.labelScale);
  const iconR = iconRadiusAt(tier, z, m.sizeScale);
  const gap = 0.7 * size; // text-offset in markers.ts, ems → px
  const cx = mercX(city.lon) * scale;
  const cy = mercY(city.lat) * scale;
  // text-max-width is 8em: longer names wrap, growing the box down instead of right.
  const rawW = textWidth(city.name, size);
  const maxW = 8 * size;
  const lines = Math.max(1, Math.ceil(rawW / maxW));
  const runW = Math.min(rawW, maxW);
  const pad = m.stablePadding;
  const textH = lines * size * 1.35;

  if (side === ANCHOR_LEFT || side === ANCHOR_RIGHT) {
    // Label beside the dot (anchor "left" = text right of dot, and vice versa).
    const halfH = Math.max(iconR, textH / 2) + pad;
    const textExtent = iconR + gap + runW + pad;
    const dotExtent = iconR + pad;
    return {
      x0: cx - (side === ANCHOR_LEFT ? dotExtent : textExtent),
      y0: cy - halfH,
      x1: cx + (side === ANCHOR_LEFT ? textExtent : dotExtent),
      y1: cy + halfH,
    };
  }
  // Label below (anchor "top") or above (anchor "bottom") the dot: union of the icon
  // square and the text block stacked vertically with the same em gap. These footprints
  // are harder to estimate exactly (centered text, line metrics), and a too-small box
  // re-opens viewport arbitration ("New Concord" flaked under pan) — so they get an
  // extra safety factor. They're <5% of placements; the density cost is negligible.
  const halfW = Math.max(iconR, runW / 2) * 1.25 + pad + 2;
  const stack = (iconR + 0.55 * size + textH) * 1.15 + pad + 2;
  const dotSide = iconR + pad;
  return {
    x0: cx - halfW,
    y0: cy - (side === ANCHOR_TOP ? dotSide : stack),
    x1: cx + halfW,
    y1: cy + (side === ANCHOR_TOP ? stack : dotSide),
  };
}

export interface StablePlacement {
  /** Band at which each city is first admitted. */
  minz: globalThis.Map<number, number>;
  /** Anchor code per admitted city, PER BAND (see ANCHOR_*): positions are re-chosen
   *  per band as neighbors join. All bands come out of the single packing pass, so a
   *  zoom-band change is a pure lookup — never a repack. */
  sides: globalThis.Map<number, globalThis.Map<number, AnchorCode>>;
}

/** Compute each city's stable minzoom, plus the label side to use at every band. `tiers`
 *  comes from the same tier assignment the marker layers use, so priority and text
 *  sizing agree with what actually renders. Trying BOTH sides of the dot before
 *  rejecting a city (side stored, rendered via data-driven text-anchor) recovers most
 *  of the density a fixed anchor gave up.
 *
 *  `townFloor` is the EFFECTIVE tier-3 minzoom (markers.ts lifts the configured floor
 *  for sparse filtered datasets) — it must match the town layer's zoom range, or the
 *  packer would admit/skip cities the layer treats oppositely.
 *
 *  The result depends only on (cities, tiers, cfg, townFloor) — no viewport, no current
 *  zoom — which is what lets markers.ts memoize it across pans, list refetches, and
 *  band changes. */
export function assignStableMinZoom(
  cities: City[],
  tiers: globalThis.Map<number, number>,
  cfg: AtlasConfig,
  townFloor: number
): StablePlacement {
  const minz = new globalThis.Map<number, number>();
  const sides = new globalThis.Map<number, globalThis.Map<number, AnchorCode>>();
  // Priority: concert count desc, id asc — fully deterministic, no viewport anywhere.
  const order = [...cities].sort((a, b) => b.count - a.count || a.id - b.id);

  const orphanRadius = cfg.markers.orphanRadius;

  for (let z = MIN_Z; z <= MAX_Z; z += BAND_STEP) {
    const scale = WORLD * 2 ** z;
    const grid = new BoxGrid(160);
    // Centres of everything admitted at THIS band, for the isolation exemption. Priority
    // order is count-desc and every tier-3 count is below tier2Min, so by the time the
    // first town is considered every capital and city has already had its say — the
    // "nearest admitted marker" test can never be answered against a half-built band.
    const admittedPts = orphanRadius > 0 ? new PointGrid(orphanRadius) : null;
    const bandSides = new globalThis.Map<number, AnchorCode>();
    sides.set(z, bandSides);
    for (const city of order) {
      const tier = tiers.get(city.id) ?? 3;
      const cx = mercX(city.lon) * scale;
      const cy = mercY(city.lat) * scale;
      const assigned = minz.get(city.id);
      const admitted = assigned !== undefined && assigned <= z;
      // Towns aren't drawn below their layer minzoom — they must not claim space they
      // can't use, or an invisible village would block a visible city's label. The
      // exemption: a town with no admitted marker within orphanRadius px isn't part of
      // any crowd, so the floor has nothing to protect and hiding it just blanks a
      // region that does have concerts (see AtlasConfig.orphanRadius).
      //
      // Tested only for NEWCOMERS. An exempted town keeps its slot at every deeper band
      // like any other admitted city — re-testing would evict it the moment a tier-2
      // neighbour joined within the radius, and "zooming only adds" is the whole
      // contract stable placement exists to keep.
      if (tier === 3 && z < townFloor && !admitted) {
        if (!admittedPts || admittedPts.hasWithin(cx, cy, orphanRadius)) continue;
      }

      let chosen: Box | null = null;
      let chosenSide: AnchorCode = ANCHOR_LEFT;
      for (const candidate of ANCHOR_CANDIDATES) {
        const box = boxAt(city, tier, z, cfg, scale, candidate);
        if (!grid.collides(box)) {
          chosen = box;
          chosenSide = candidate;
          break;
        }
      }

      if (admitted) {
        // Already-admitted cities are never evicted: if no position is free (an
        // earlier-priority newcomer squeezed in), keep the default box and let the
        // collision backstop arbitrate the rare true overlap.
        grid.insert(chosen ?? boxAt(city, tier, z, cfg, scale, ANCHOR_LEFT));
        bandSides.set(city.id, chosen ? chosenSide : ANCHOR_LEFT);
        admittedPts?.insert(cx, cy);
      } else if (chosen) {
        minz.set(city.id, z);
        grid.insert(chosen);
        bandSides.set(city.id, chosenSide);
        admittedPts?.insert(cx, cy);
      }
    }
  }

  // Whatever never won a slot appears at max zoom; MapLibre's collision backstop
  // arbitrates any residual crowding there.
  for (const city of cities) {
    if (!minz.has(city.id)) minz.set(city.id, MAX_Z);
  }
  return { minz, sides };
}
